import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Save, MessageSquare, Building2, Lock, CircleDot, Send, KeyRound, Gift, ShieldCheck, Bell, Download, Inbox, Server, ShieldAlert, FileText, Megaphone } from "lucide-react";
import MfaSetupDialog from "@/components/MfaSetupDialog";
import RecoveryCodesBanner from "@/components/RecoveryCodesBanner";
import CronHistoryPanel from "@/components/admin/CronHistoryPanel";
import { DenialLogPanel, SmsOutboxPanel } from "@/components/admin/SecurityPanels";
import { getUser, BACKEND_URL } from "@/lib/api";

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div className="glass-card rounded-2xl p-6 gold-border" data-testid={testid}>
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/20 grid place-items-center">
        <Icon className="w-5 h-5 text-amber-400" />
      </div>
      <div>
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
        {subtitle && <p className="text-sm text-slate-400">{subtitle}</p>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const StatusPill = ({ ok, labelOn = "Live", labelOff = "Not configured" }) => (
  <Badge className={`${ok ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300" : "bg-slate-400/10 border-slate-400/20 text-slate-400"} font-mono text-[10px] uppercase tracking-wider`}>
    <CircleDot className={`w-3 h-3 mr-1 ${ok ? "text-emerald-400" : "text-slate-500"}`} /> {ok ? labelOn : labelOff}
  </Badge>
);

export default function SettingsTab() {
  const currentUser = getUser();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [smsTest, setSmsTest] = useState("");
  const [prefs, setPrefs] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [legal, setLegal] = useState({ terms_version: null, terms_updated_at: null, history: [] });
  const [termsForm, setTermsForm] = useState({ version: "", note: "" });

  const load = async () => {
    try {
      const [{ data }, { data: n }] = await Promise.all([api.get("/settings"), api.get("/auth/notifications")]);
      setS(data); setPrefs(n);
    } catch { toast.error("Failed to load settings"); }
    try {
      const { data: lg } = await api.get("/admin/legal/version");
      setLegal(lg || { history: [] });
      if (!termsForm.version && lg?.terms_version) setTermsForm((f) => ({ ...f, version: lg.terms_version }));
    } catch { /* legal endpoint is super-admin only, silent */ }
  };
  useEffect(() => { load(); }, []);

  const broadcastTerms = async () => {
    const v = (termsForm.version || "").trim();
    if (!v) { toast.error("Give this update a version tag first"); return; }
    if (!window.confirm(`Email every rider that Terms v${v} is now live?`)) return;
    setSaving("terms");
    try {
      const { data } = await api.post("/admin/legal/broadcast-terms-update", termsForm);
      toast.success(`Sent to ${data.sent} rider${data.sent === 1 ? "" : "s"}${data.failed ? ` (${data.failed} failed)` : ""}`);
      setTermsForm({ version: v, note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setSaving(null); }
  };

  if (!s) return <div className="text-slate-500 p-8">Loading settings…</div>;

  const saveGeneral = async () => {
    setSaving("general");
    try {
      await api.patch("/settings/general", s.general);
      toast.success("Company settings saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const saveSms = async () => {
    setSaving("sms");
    try {
      const payload = { sms_enabled: s.sms.sms_enabled };
      // Only send credentials if user typed a fresh value (masks contain bullets)
      if (s.sms.twilio_account_sid && !s.sms.twilio_account_sid.includes("•")) payload.twilio_account_sid = s.sms.twilio_account_sid;
      if (s.sms.twilio_auth_token && !s.sms.twilio_auth_token.includes("•")) payload.twilio_auth_token = s.sms.twilio_auth_token;
      if (s.sms.twilio_from_number) payload.twilio_from_number = s.sms.twilio_from_number;
      await api.patch("/settings/sms", payload);
      toast.success("SMS settings saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const saveLoyalty = async () => {
    setSaving("loyalty");
    try {
      await api.put("/settings/loyalty", {
        enabled: s.loyalty.enabled,
        every_n_rides: Number(s.loyalty.every_n_rides) || 10,
        discount_percent: Number(s.loyalty.discount_percent) || 50,
      });
      toast.success("Loyalty programme saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const testSms = async () => {
    if (!smsTest.startsWith("+")) { toast.error("Use E.164 format like +64211234567"); return; }
    try {
      const { data } = await api.post("/settings/sms/test", { to: smsTest });
      toast.success(data.note || (data.ok ? "SMS sent" : "Logged (mock mode)"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setSaving("password");
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(null); }
  };

  const savePrefs = async () => {
    setSaving("prefs");
    try {
      await api.patch("/auth/notifications", prefs);
      toast.success("Notification preferences saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(null); }
  };

  const saveDigest = async () => {
    setSaving("digest");
    try {
      const d = s.digest || {};
      await api.patch("/settings/digest", {
        enabled: !!d.enabled,
        frequency: d.frequency || "hourly",
        mode: d.mode || "digest_only",
        max_bookings: Number(d.max_bookings) || 100,
        vip_instant_enabled: d.vip_instant_enabled !== false,
      });
      toast.success("Digest settings saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(null); }
  };

  const exportZip = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("apex_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/export.zip`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `triptap-export-${Date.now()}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div>
        <div className="text-caption mb-1">System</div>
        <h1 className="font-display text-3xl font-extrabold">Settings</h1>
        <p className="text-slate-400 mt-1 text-sm">Business profile, SMS provider, loyalty, security — one control panel.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="settings-integrations">
        <IntegrationChip label="Email" ok={s.integrations.email_configured} />
        <IntegrationChip label="Stripe" ok={s.integrations.stripe_configured} />
        <IntegrationChip label="Twilio SMS" ok={s.sms.configured} />
      </div>

      {/* COMPANY */}
      <Section icon={Building2} title="Company profile" subtitle="Shown in emails, PDFs, and the site header" testid="settings-general">
        <Grid>
          <Field label="Company name"><Input value={s.general.company_name} onChange={(e) => setS({ ...s, general: { ...s.general, company_name: e.target.value } })} data-testid="set-company-name" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="Phone"><Input value={s.general.company_phone} onChange={(e) => setS({ ...s, general: { ...s.general, company_phone: e.target.value } })} data-testid="set-company-phone" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="Logo URL" full><Input value={s.general.company_logo_url} onChange={(e) => setS({ ...s, general: { ...s.general, company_logo_url: e.target.value } })} data-testid="set-company-logo" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="Address" full><Input value={s.general.company_address} onChange={(e) => setS({ ...s, general: { ...s.general, company_address: e.target.value } })} data-testid="set-company-address" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="Brand primary color (Hex)">
            <div className="flex gap-2 items-center">
              <Input
                type="color"
                value={s.general.brand_primary_color || "#E5A93C"}
                onChange={(e) => setS({ ...s, general: { ...s.general, brand_primary_color: e.target.value } })}
                className="bg-black/30 border-white/10 h-11 w-16 p-1 cursor-pointer"
                data-testid="set-brand-color-picker"
              />
              <Input
                value={s.general.brand_primary_color || "#E5A93C"}
                onChange={(e) => setS({ ...s, general: { ...s.general, brand_primary_color: e.target.value } })}
                placeholder="#E5A93C"
                className="bg-black/30 border-white/10 h-11 font-mono flex-1"
                data-testid="set-brand-color"
              />
            </div>
          </Field>
          <Field label="Email footer (shown in every notification)" full>
            <textarea
              value={s.general.brand_email_footer || ""}
              onChange={(e) => setS({ ...s, general: { ...s.general, brand_email_footer: e.target.value } })}
              placeholder="e.g., ABN 12 345 · Auckland NZ · Reply to this email for support"
              rows={2}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-y"
              data-testid="set-brand-footer"
            />
          </Field>
          <Field label="Tax label (used on services & receipts)">
            <select
              value={s.general.tax_label || "GST"}
              onChange={(e) => setS({ ...s, general: { ...s.general, tax_label: e.target.value } })}
              className="w-full bg-black/30 border border-white/10 h-11 rounded-md px-3 text-sm text-white"
              data-testid="set-tax-label"
            >
              <option value="GST">GST</option>
              <option value="VAT">VAT</option>
              <option value="Tax">Tax</option>
              <option value="Sales Tax">Sales Tax</option>
            </select>
          </Field>
          <Field label="Default estimated distance (km)"><Input type="number" step="0.5" value={s.general.default_estimated_km} onChange={(e) => setS({ ...s, general: { ...s.general, default_estimated_km: Number(e.target.value) } })} data-testid="set-default-km" className="bg-black/30 border-white/10 h-11" /></Field>
        </Grid>
        <div className="flex justify-end pt-2">
          <Button onClick={saveGeneral} disabled={saving === "general"} className="btn-gold" data-testid="set-general-save"><Save className="w-4 h-4 mr-2" /> {saving === "general" ? "Saving…" : "Save company"}</Button>
        </div>
      </Section>

      {/* SMS */}
      <Section icon={MessageSquare} title="SMS provider (Twilio)" subtitle="Powers driver-assigned + 5-min ETA texts" testid="settings-sms">
        <div className={`flex items-center justify-between gap-4 rounded-lg p-4 border ${s.sms.configured ? "bg-emerald-500/5 border-emerald-400/30" : "bg-amber-500/5 border-amber-400/20"}`}>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium flex items-center gap-2">
              {s.sms.configured ? (
                <>
                  <span className="inline-flex w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Live via Twilio
                </>
              ) : (
                <>
                  <span className="inline-flex w-2 h-2 rounded-full bg-amber-400" />
                  Mock mode — SMS is logged only
                </>
              )}
            </div>
            <div className="text-xs text-slate-400 mt-1 leading-relaxed">
              {s.sms.configured
                ? `Sending from ${s.sms.twilio_from_number}. Driver-assigned + 5-min ETA texts are hitting real phones.`
                : "Paste your Account SID, Auth Token, and Twilio number below to switch on real SMS."}
              {" "}<a href="https://console.twilio.com/us1/account/keys-credentials/api-keys" target="_blank" rel="noreferrer" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">Get your keys →</a>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <StatusPill ok={s.sms.configured} labelOn="Live" labelOff="Mock" />
            <Switch checked={s.sms.sms_enabled} onCheckedChange={(v) => setS({ ...s, sms: { ...s.sms, sms_enabled: v } })} data-testid="set-sms-toggle" />
          </div>
        </div>
        <Grid>
          <Field label="Twilio Account SID" full><Input value={s.sms.twilio_account_sid} onChange={(e) => setS({ ...s, sms: { ...s.sms, twilio_account_sid: e.target.value } })} placeholder="ACxxxxxxxx…" data-testid="set-twilio-sid" className="bg-black/30 border-white/10 h-11 font-mono" /></Field>
          <Field label="Twilio Auth Token" full><Input type="password" value={s.sms.twilio_auth_token} onChange={(e) => setS({ ...s, sms: { ...s.sms, twilio_auth_token: e.target.value } })} placeholder="32-char hex" data-testid="set-twilio-token" className="bg-black/30 border-white/10 h-11 font-mono" /></Field>
          <Field label="From number (E.164)" full><Input value={s.sms.twilio_from_number} onChange={(e) => setS({ ...s, sms: { ...s.sms, twilio_from_number: e.target.value } })} placeholder="+64…" data-testid="set-twilio-from" className="bg-black/30 border-white/10 h-11 font-mono" /></Field>
        </Grid>
        <div className="flex flex-wrap justify-between gap-2 pt-2">
          <div className="flex gap-2 flex-1 min-w-[280px]">
            <Input value={smsTest} onChange={(e) => setSmsTest(e.target.value)} placeholder="+64211234567" data-testid="set-sms-test-input" className="bg-black/30 border-white/10 h-11 font-mono max-w-xs" />
            <Button variant="outline" onClick={testSms} className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10" data-testid="set-sms-test-send"><Send className="w-4 h-4 mr-2" /> Send test</Button>
          </div>
          <Button onClick={saveSms} disabled={saving === "sms"} className="btn-gold" data-testid="set-sms-save"><Save className="w-4 h-4 mr-2" /> {saving === "sms" ? "Saving…" : "Save SMS"}</Button>
        </div>
      </Section>

      {/* LOYALTY */}
      <Section icon={Gift} title="Loyalty programme" subtitle="Auto-discount every Nth completed rider trip" testid="settings-loyalty">
        <div className="flex items-center justify-between gap-4 bg-black/20 border border-white/5 rounded-lg p-4">
          <div>
            <div className="text-sm font-medium">Enable loyalty rewards</div>
            <div className="text-xs text-slate-500 mt-0.5">Auto-applies discount at booking time</div>
          </div>
          <Switch checked={s.loyalty.enabled} onCheckedChange={(v) => setS({ ...s, loyalty: { ...s.loyalty, enabled: v } })} data-testid="set-loyalty-toggle" />
        </div>
        <Grid>
          <Field label="Every N rides"><Input type="number" min="1" value={s.loyalty.every_n_rides} onChange={(e) => setS({ ...s, loyalty: { ...s.loyalty, every_n_rides: e.target.value } })} data-testid="set-loyalty-n" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="Discount %"><Input type="number" min="1" max="100" value={s.loyalty.discount_percent} onChange={(e) => setS({ ...s, loyalty: { ...s.loyalty, discount_percent: e.target.value } })} data-testid="set-loyalty-percent" className="bg-black/30 border-white/10 h-11" /></Field>
        </Grid>
        <div className="flex justify-end pt-2">
          <Button onClick={saveLoyalty} disabled={saving === "loyalty"} className="btn-gold" data-testid="set-loyalty-save"><Save className="w-4 h-4 mr-2" /> {saving === "loyalty" ? "Saving…" : "Save loyalty"}</Button>
        </div>
      </Section>

      {/* PASSWORD */}
      <Section icon={KeyRound} title="Change your password" subtitle="Applies to your own admin account" testid="settings-password">
        <form onSubmit={changePassword} className="space-y-3 max-w-md">
          <Field label="Current password"><Input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="set-pw-current" className="bg-black/30 border-white/10 h-11" /></Field>
          <Field label="New password (min 8)"><Input type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="set-pw-new" className="bg-black/30 border-white/10 h-11" /></Field>
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving === "password"} className="btn-gold" data-testid="set-pw-save"><Lock className="w-4 h-4 mr-2" /> {saving === "password" ? "Updating…" : "Update password"}</Button>
          </div>
        </form>
      </Section>

      {/* Recovery codes low-count warning (renders only when MFA on + codes <= threshold) */}
      <RecoveryCodesBanner />

      {/* LEGAL BROADCAST — bump the terms/privacy version and email every rider so old consents don't drift */}
      <Section icon={FileText} title="Terms & Privacy updates" subtitle="Notify every rider when the legal documents change so their consent stays current" testid="settings-legal">
        <div className="grid sm:grid-cols-2 gap-3 bg-black/20 border border-white/5 rounded-lg p-4">
          <div>
            <div className="text-xs uppercase tracking-widest font-mono text-slate-500">Current version</div>
            <div className="mt-1 font-display text-2xl text-amber-400" data-testid="legal-current-version">
              {legal.terms_version ? `v${legal.terms_version}` : "—"}
            </div>
            {legal.terms_updated_at && (
              <div className="text-xs text-slate-500 font-mono mt-0.5">Broadcast {new Date(legal.terms_updated_at).toLocaleString()}</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-mono text-slate-500">Recent broadcasts</div>
            <div className="mt-1 space-y-1 max-h-24 overflow-y-auto text-xs text-slate-400 font-mono" data-testid="legal-history">
              {(legal.history || []).length === 0 && <div className="text-slate-600">No broadcasts yet.</div>}
              {(legal.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex justify-between gap-2">
                  <span>v{h.version}</span>
                  <span className="text-slate-500 truncate">{new Date(h.at).toLocaleDateString()} · {h.actor}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <Grid>
          <Field label="New version tag (e.g. 2026-02)">
            <Input value={termsForm.version} onChange={(e) => setTermsForm({ ...termsForm, version: e.target.value })} placeholder="2026-02" data-testid="legal-version-input" className="bg-black/30 border-white/10 h-11" />
          </Field>
          <Field label="What's changing? (optional, shown in email)">
            <Input value={termsForm.note} onChange={(e) => setTermsForm({ ...termsForm, note: e.target.value })} placeholder="Clarified cancellation policy…" data-testid="legal-note-input" className="bg-black/30 border-white/10 h-11" />
          </Field>
        </Grid>
        <div className="flex justify-end pt-2">
          <Button onClick={broadcastTerms} disabled={saving === "terms"} className="btn-gold" data-testid="legal-broadcast-btn">
            <Megaphone className="w-4 h-4 mr-2" /> {saving === "terms" ? "Sending…" : "Broadcast to all riders"}
          </Button>
        </div>
      </Section>

      {/* SECURITY / MFA */}
      <Section icon={ShieldCheck} title="Two-factor authentication (MFA)" subtitle="Require a 6-digit code from your authenticator app on sign-in" testid="settings-mfa">
        <div className="flex items-center justify-between gap-4 bg-black/20 border border-white/5 rounded-lg p-4">
          <div>
            <div className="text-sm font-medium">Status</div>
            <div className="text-xs text-slate-500 mt-0.5">{currentUser?.mfa_enabled ? "MFA is protecting your account" : "MFA is currently off"}</div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill ok={currentUser?.mfa_enabled} labelOn="Enabled" labelOff="Off" />
            <MfaSetupDialog user={currentUser} onDone={() => { const u = getUser(); if (u) localStorage.setItem("apex_user", JSON.stringify({ ...u, mfa_enabled: !u.mfa_enabled })); load(); }} />
          </div>
        </div>
      </Section>

      {/* NOTIFICATION DIGEST */}
      <Section icon={Inbox} title="New-booking digest" subtitle="Batch new-booking alerts into a scheduled email so admins don't get flooded" testid="settings-digest">
        <div className="flex items-center justify-between gap-4 bg-black/20 border border-white/5 rounded-lg p-4">
          <div>
            <div className="text-sm font-medium">Enable digest mode</div>
            <div className="text-xs text-slate-500 mt-0.5">When on, new bookings are queued and emailed on schedule instead of instantly</div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill ok={!!s.digest?.enabled} labelOn="Active" labelOff="Off" />
            <Switch checked={!!s.digest?.enabled} onCheckedChange={(v) => setS({ ...s, digest: { ...(s.digest || {}), enabled: v } })} data-testid="set-digest-toggle" />
          </div>
        </div>
        <Grid>
          <Field label="Frequency">
            <select
              value={s.digest?.frequency || "hourly"}
              onChange={(e) => setS({ ...s, digest: { ...(s.digest || {}), frequency: e.target.value } })}
              disabled={!s.digest?.enabled}
              className="w-full bg-black/30 border border-white/10 h-11 rounded-md px-3 text-sm text-white disabled:opacity-50"
              data-testid="set-digest-frequency"
            >
              <option value="hourly">Hourly (recommended)</option>
              <option value="every_2h">Every 2 hours</option>
              <option value="daily">Daily summary</option>
            </select>
          </Field>
          <Field label="Delivery mode">
            <select
              value={s.digest?.mode || "digest_only"}
              onChange={(e) => setS({ ...s, digest: { ...(s.digest || {}), mode: e.target.value } })}
              disabled={!s.digest?.enabled}
              className="w-full bg-black/30 border border-white/10 h-11 rounded-md px-3 text-sm text-white disabled:opacity-50"
              data-testid="set-digest-mode"
            >
              <option value="digest_only">Digest only</option>
              <option value="digest_plus_instant">Digest + instant alerts</option>
            </select>
          </Field>
          <Field label="Max bookings per digest">
            <Input
              type="number" min="1" max="500"
              value={s.digest?.max_bookings ?? 100}
              onChange={(e) => setS({ ...s, digest: { ...(s.digest || {}), max_bookings: Number(e.target.value) || 100 } })}
              disabled={!s.digest?.enabled}
              className="bg-black/30 border-white/10 h-11 disabled:opacity-50"
              data-testid="set-digest-max"
            />
          </Field>
          <Field label="Last digest sent">
            <div className="h-11 rounded-md border border-white/5 bg-black/20 px-3 flex items-center text-xs text-slate-400 font-mono" data-testid="set-digest-last-run">
              {s.digest?.last_run_at ? new Date(s.digest.last_run_at).toLocaleString() : "Never"}
            </div>
          </Field>
        </Grid>
        <div className="flex items-center justify-between gap-4 bg-black/20 border border-amber-400/20 rounded-lg p-4 mt-2">
          <div>
            <div className="text-sm font-medium flex items-center gap-2">
              VIP bypass
              <span className="px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-300 text-[9px] font-mono uppercase tracking-widest">Recommended</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Corporate & airport bookings skip the queue and always get an instant email</div>
          </div>
          <Switch
            checked={s.digest?.vip_instant_enabled !== false}
            onCheckedChange={(v) => setS({ ...s, digest: { ...(s.digest || {}), vip_instant_enabled: v } })}
            disabled={!s.digest?.enabled}
            data-testid="set-digest-vip-toggle"
          />
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={saveDigest} disabled={saving === "digest"} className="btn-gold" data-testid="set-digest-save">
            <Save className="w-4 h-4 mr-2" /> {saving === "digest" ? "Saving…" : "Save digest"}
          </Button>
        </div>
      </Section>

      {/* NOTIFICATION PREFS */}
      {prefs && (
        <Section icon={Bell} title="Email alerts" subtitle="Choose which events reach your inbox" testid="settings-notifications">
          <div className="space-y-2">
            {[
              ["new_bookings", "New booking created", "Ping whenever a rider submits a new booking"],
              ["corp_rider_onboards", "New corporate rider", "First-time booking from a corporate domain"],
              ["lockouts", "Security lockouts", "Admin account frozen after 5 failed sign-ins"],
              ["revenue_alerts", "Daily revenue digest", "End-of-day revenue and trip totals"],
            ].map(([k, title, desc]) => (
              <div key={k} className="flex items-center justify-between gap-4 bg-black/20 border border-white/5 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium">{title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
                </div>
                <Switch checked={!!prefs[k]} onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} data-testid={`set-pref-${k}`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={savePrefs} disabled={saving === "prefs"} className="btn-gold" data-testid="set-prefs-save"><Save className="w-4 h-4 mr-2" /> {saving === "prefs" ? "Saving…" : "Save preferences"}</Button>
          </div>
        </Section>
      )}

      {/* SYSTEM JOBS (crons) */}
      <Section icon={Server} title="System jobs" subtitle="Scheduled crons — last-run status, history, and manual triggers" testid="settings-system-jobs">
        <CronHistoryPanel />
      </Section>

      {/* SECURITY — permission denials */}
      <Section icon={ShieldAlert} title="Permission denials" subtitle="Last 100 blocked API calls — spot permission probing early" testid="settings-denials">
        <DenialLogPanel />
      </Section>

      {/* SMS SIMULATOR / OUTBOX */}
      <Section icon={MessageSquare} title="SMS outbox" subtitle="Preview what we would (or did) send — driver assignments, surge nudges, ETA bumps" testid="settings-sms-outbox">
        <SmsOutboxPanel />
      </Section>

      {/* EXPORT */}
      <Section icon={Download} title="Backup & export" subtitle="Download bookings, invoices (PDFs), and drivers as a single ZIP" testid="settings-export">
        <div className="flex items-center justify-between gap-4 bg-black/20 border border-white/5 rounded-lg p-4">
          <div>
            <div className="text-sm font-medium">One-click export</div>
            <div className="text-xs text-slate-500 mt-0.5">Includes bookings.csv, invoices.csv + all invoice PDFs, drivers.csv, README.txt</div>
          </div>
          <Button onClick={exportZip} disabled={exporting} className="btn-gold" data-testid="set-export-zip"><Download className="w-4 h-4 mr-2" /> {exporting ? "Preparing…" : "Download ZIP"}</Button>
        </div>
      </Section>
    </div>
  );
}

const Grid = ({ children }) => <div className="grid sm:grid-cols-2 gap-4">{children}</div>;

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const IntegrationChip = ({ label, ok }) => (
  <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 border text-xs font-mono ${ok ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-300" : "border-slate-500/30 bg-slate-500/5 text-slate-400"}`}>
    <CircleDot className={`w-3.5 h-3.5 ${ok ? "text-emerald-400" : "text-slate-500"}`} />
    {label}: <span className="font-bold">{ok ? "Live" : "Off"}</span>
  </div>
);
